// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Records hook with mode routing.
 *
 * Delegates to either local RxDB (useLocalRecords) or remote D1
 * (useRemoteQueryConditional + useRemoteMutationConditional)
 * based on the CollectionModeProvider context.
 *
 * When no provider is mounted, all collections route to local (backward compatible).
 */

import { useCallback } from 'react'
import { useCollectionMode } from '../CollectionModeContext'
import { useLocalRecords } from './useLocalRecords'
import { useRemoteQueryConditional } from './useRemoteQuery'
import { useRemoteMutationConditional } from './useRemoteMutation'
import type { UseLocalRecordsResult } from './useLocalRecords'

export type UseRecordsOptions = import('./useLocalRecords').UseLocalRecordsOptions

export type UseRecordsResult<T = Record<string, unknown>> = UseLocalRecordsResult<T>

/**
 * Generic React hook for querying and mutating virtual collections.
 *
 * Routes to local RxDB or remote D1 based on CollectionModeProvider context.
 * When no provider is mounted, all collections route to local.
 *
 * @param db - RxDB database instance (must have `schemas` and `records` collections)
 * @param schemaName - Name of the virtual collection schema (e.g. 'products', 'codebases')
 * @param options - Query options: selector, sort, limit, skip, orgId
 * @returns Reactive data array, loading/error state, and CRUD mutations
 */
export function useRecords<T = Record<string, unknown>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: Record<string, any> | null | undefined,
  schemaName: string,
  options: UseRecordsOptions = {}
): UseRecordsResult<T> {
  const modeCtx = useCollectionMode()
  const mode = modeCtx?.getMode(schemaName) ?? 'local'
  const isRemote = mode === 'remote'

  // Both hook sets always called (rules of hooks), only one is active.
  const localResult = useLocalRecords<T>(
    isRemote ? null : db,
    schemaName,
    options
  )

  const remoteQuery = useRemoteQueryConditional<T>(
    isRemote ? schemaName : null,
    {
      selector: options.selector,
      sort: options.sort as Array<Record<string, 'asc' | 'desc'>> | undefined,
      limit: options.limit,
      skip: options.skip,
    }
  )

  const remoteMutation = useRemoteMutationConditional<T & { id: string }>(
    isRemote ? schemaName : null
  )

  // Remote refresh invalidates the react-query cache
  const remoteRefresh = useCallback(() => {
    if (modeCtx) {
      modeCtx.queryClient.invalidateQueries({ queryKey: [schemaName] })
    }
  }, [modeCtx, schemaName])

  if (isRemote) {
    return {
      data: remoteQuery.data,
      isLoading: remoteQuery.isLoading || remoteMutation.isLoading,
      error: remoteQuery.error ?? remoteMutation.error,
      create: remoteMutation.create as UseRecordsResult<T>['create'],
      update: remoteMutation.update as unknown as UseRecordsResult<T>['update'],
      remove: remoteMutation.remove,
      refresh: remoteRefresh,
    }
  }

  return localResult
}
